import React from "react";
import {
    FiBookmark,
    FiInbox,
    FiMessageCircle,
    FiRefreshCw,
    FiShield,
    FiSliders,
} from "react-icons/fi";
import { Link } from "react-router-dom";

import { COMMENT_REPORTING_UI_ENABLED } from "../Constants/FeatureFlags";
import { RoutePaths } from "../Constants/RoutePaths";
import { CommentsController } from "../Hooks/Comments/useContentComments";
import { CommentContentType } from "../Api/CommentsAPI";
import {
    VideoCommentPinAttributionDTO,
    VideoCommentSort,
} from "../Models/DTOs/VideoCommentsDTO";
import VideoCommentCard from "./UI/Comments/VideoCommentCard";
import VideoCommentComposer from "./UI/Comments/VideoCommentComposer";
import VideoCommentThread from "./UI/Comments/VideoCommentThread";

interface CommentsDiscussionProps {
    controller: CommentsController;
    contentType: CommentContentType;
    onComposerFocusChange?: (focused: boolean) => void;
}

const SORT_OPTIONS: Array<{
    value: VideoCommentSort;
    label: string;
    description: string;
}> = [
    { value: "top", label: "Top", description: "Highest net votes" },
    { value: "newest", label: "Newest", description: "Most recent first" },
];

export const pinnedByLabel = (
    pinnedBy: VideoCommentPinAttributionDTO | null
): string => {
    if (pinnedBy?.display_name) return `Pinned by ${pinnedBy.display_name}`;
    if (pinnedBy?.kind === "video_author") return "Pinned by the video author";
    if (pinnedBy?.kind === "article_author") return "Pinned by the article author";
    return "Pinned by a moderator";
};

const CommentsDiscussion: React.FC<CommentsDiscussionProps> = ({
    controller,
    contentType,
    onComposerFocusChange,
}) => {
    const headingId = `${contentType}-comments-heading`;
    const { state } = controller;
    const hasComments = Boolean(state.pinnedComment || state.items.length);
    // False permissions are not a membership decision until a successful read
    // identifies the viewer. Loading/errors and anonymous fallbacks are distinct.
    const permissionsReady = state.permissionsLoaded && !state.loading && !state.error;
    // Keep an authorized editor mounted during background refreshes so votes and
    // pin changes cannot discard an unsent draft. Auth/content resets clear this.
    const canCompose = state.permissionsLoaded && state.permissions.can_comment &&
        !state.permissions.unavailable;
    const showAccessRetry = permissionsReady && (
        state.permissions.unavailable ||
        (!state.permissions.can_comment && controller.isAuthenticated && state.permissions.is_authenticated !== true)
    );

    const renderRoot = (
        comment: NonNullable<typeof state.pinnedComment>,
        showPinnedBadge = true
    ) => (
        <div className="video-comments__root" key={comment.id}>
            <VideoCommentCard
                comment={comment}
                isRoot
                showPinnedBadge={showPinnedBadge}
                controller={controller}
                onFocusChange={onComposerFocusChange}
            />
            <VideoCommentThread
                root={comment}
                controller={controller}
                onFocusChange={onComposerFocusChange}
            />
        </div>
    );

    return (
        <section className="video-comments" aria-labelledby={headingId}>
            <div className="video-comments__heading-row">
                <div className="video-comments__title">
                    <span className="video-comments__title-icon" aria-hidden="true">
                        <FiMessageCircle />
                    </span>
                    <div>
                        <div className="video-comments__title-line">
                            <h2 id={headingId}>Comments</h2>
                            <span className="video-comments__count">
                                {state.total.toLocaleString()} {state.total === 1 ? "thread" : "threads"}
                            </span>
                        </div>
                        <p>Share ideas, ask questions, and continue the conversation.</p>
                    </div>
                </div>

                <div className="video-comments__heading-actions">
                    {COMMENT_REPORTING_UI_ENABLED && controller.isModerator && (
                        <Link
                            className="video-comments__moderation-link"
                            to={contentType === "article" ? `${RoutePaths.CommentModeration}?type=article` : RoutePaths.CommentModeration}
                        >
                            <FiShield aria-hidden="true" />
                            <span>Moderation</span>
                        </Link>
                    )}
                    <div
                        className="video-comments__sort"
                        role="group"
                        aria-label="Sort comments"
                    >
                        <span className="video-comments__sort-label" aria-hidden="true">
                            <FiSliders />
                            <span>Sort</span>
                        </span>
                        <div className="video-comments__sort-options">
                            {SORT_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    className={`video-comments__sort-option ${
                                        state.sort === option.value ? "is-active" : ""
                                    }`}
                                    aria-pressed={state.sort === option.value}
                                    title={option.description}
                                    onFocus={() => onComposerFocusChange?.(true)}
                                    onBlur={() => onComposerFocusChange?.(false)}
                                    onClick={() => controller.setSort(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {canCompose ? (
                <VideoCommentComposer
                    onSubmit={(body, idempotencyKey) =>
                        controller.createComment(body, idempotencyKey)
                    }
                    onFocusChange={onComposerFocusChange}
                />
            ) : permissionsReady && (showAccessRetry ? (
                <div className="video-comments__participation-note" role="status">
                    <FiMessageCircle aria-hidden="true" />
                    <div>
                        <p>
                            {state.permissions.unavailable
                                ? "Comment access is temporarily unavailable. Please try again."
                                : "We could not verify your signed-in session for comments. Try again or sign in again."}
                        </p>
                        <button type="button" className="video-comments__load-more" onClick={() => void controller.refreshRoots()}>
                            Retry access check
                        </button>
                        {!state.permissions.unavailable && <Link to={RoutePaths.Login}>Sign in again</Link>}
                    </div>
                </div>
            ) : state.permissions.is_authenticated === true ? (
                <div className="video-comments__participation-note">
                    <FiMessageCircle aria-hidden="true" />
                    <p>
                        An active membership is required to comment and vote.
                        {COMMENT_REPORTING_UI_ENABLED &&
                            " You can still report content that violates our guidelines."}
                    </p>
                </div>
            ) : (
                <div className="video-comments__participation-note">
                    <FiMessageCircle aria-hidden="true" />
                    <p>
                        <Link to={RoutePaths.Login}>Sign in</Link> to join the
                        conversation.
                    </p>
                </div>
            ))}

            {(state.loading || !state.permissionsLoaded) && !state.error && !hasComments && (
                <div className="video-comments__status" role="status">
                    <span className="video-comments__spinner" aria-hidden="true" />
                    <span>Loading discussion…</span>
                </div>
            )}

            {state.error && (
                <div className="video-comments__error" role="alert">
                    <div>
                        <strong>We couldn’t load the discussion.</strong>
                        <p>{state.error}</p>
                    </div>
                    <button type="button" onClick={() => void controller.refreshRoots()}>
                        <FiRefreshCw aria-hidden="true" />
                        <span>Try again</span>
                    </button>
                </div>
            )}

            {permissionsReady && !hasComments && (
                <div className="video-comments__empty">
                    <span className="video-comments__empty-icon" aria-hidden="true">
                        <FiInbox />
                    </span>
                    <h3>Start the conversation</h3>
                    <p>Be the first to share a thought about this {contentType}.</p>
                </div>
            )}

            {state.pinnedComment && (
                <div className="video-comments__pinned">
                    <div className="video-comments__pinned-label">
                        <FiBookmark aria-hidden="true" />
                        <span>{pinnedByLabel(state.pinnedComment.pinned_by)}</span>
                    </div>
                    {renderRoot(state.pinnedComment, false)}
                </div>
            )}

            {state.items.length > 0 && (
                <div className="video-comments__list">
                    {state.items.map((comment) => renderRoot(comment))}
                </div>
            )}

            {state.nextCursor && (
                <button
                    type="button"
                    className="video-comments__load-more"
                    disabled={state.loadingMore}
                    onClick={() => void controller.loadMoreRoots()}
                >
                    {state.loadingMore && (
                        <span className="video-comments__spinner" aria-hidden="true" />
                    )}
                    <span>{state.loadingMore ? "Loading…" : "Load more comments"}</span>
                </button>
            )}
        </section>
    );
};

export default CommentsDiscussion;
